const WORKFLOW_ACTIONS = Object.freeze(["approve", "apply", "rollback"]);
const WORKFLOW_ACTION_SET = new Set(WORKFLOW_ACTIONS);
const WORKFLOW_STATUSES = new Set([
  "pending_approval",
  "approved",
  "applied",
  "rolled_back",
  "blocked",
]);

const CAPABILITY_BY_ACTION = Object.freeze({
  approve: "can_approve",
  apply: "can_apply",
  rollback: "can_rollback",
});

const ACTION_STATUS_COPY = Object.freeze({
  approve: {
    title: "Approve recommendation run",
    confirm: "Approve full run",
  },
  apply: {
    title: "Apply approved recommendation run",
    confirm: "Apply full run",
  },
  rollback: {
    title: "Rollback complete application",
    confirm: "Rollback full application",
  },
});

const WORKFLOW_ERROR_COPY = Object.freeze({
  RMS_WORKFLOW_PIPELINE_NOT_CURRENT:
    "The pipeline is no longer current. Refresh and review the new run.",
  RMS_WORKFLOW_STALE_APPROVAL:
    "The approval is stale. Generate a new preview and approve the run again.",
  RMS_WORKFLOW_INVALID_STATE:
    "This action is no longer valid for the current workflow state.",
  RMS_WORKFLOW_VERSION_CONFLICT:
    "The workflow changed while you were reviewing it. Refresh before continuing.",
  RMS_WORKFLOW_PLAN_CONFLICT:
    "The execution plan changed. Generate a new preview before continuing.",
  RMS_WORKFLOW_PRICE_DRIFT:
    "One or more live prices changed. Review a fresh preview before continuing.",
  RMS_WORKFLOW_ROLLBACK_DRIFT:
    "The live rate state has changed since application. Rollback was safely blocked.",
  RMS_WORKFLOW_NOTHING_TO_APPLY:
    "There are no executable changes in this recommendation run.",
  RMS_WORKFLOW_UNSUPPORTED_RECOMMENDATION_CONTRACT:
    "Only a complete recommendation run using contract v6 can be operated.",
  RMS_WORKFLOW_LINEAGE_INVALID:
    "The recommendation lineage could not be verified. The run cannot be operated.",
  RMS_WORKFLOW_RATE_PRODUCT_NOT_EXECUTABLE:
    "At least one rate product is not safe to execute.",
  RMS_WORKFLOW_STOP_SELL_ACTIVE:
    "At least one proposed change is protected by an active stop-sell.",
  IDEMPOTENCY_CONFLICT:
    "This operation key was already used with different data. Refresh before retrying.",
  RMS_WORKFLOW_IDEMPOTENCY_CONFLICT:
    "This operation key was already used with different data. Refresh before retrying.",
});

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizePositiveId(value) {
  const numeric = Number(value);
  return Number.isSafeInteger(numeric) && numeric > 0 ? numeric : null;
}

function normalizeBlockers(value) {
  if (!Array.isArray(value)) return null;
  return value.map((blocker) => {
    if (typeof blocker === "string") {
      return { code: blocker, message: blocker, details: null };
    }
    if (!isPlainObject(blocker)) return null;
    const code = String(blocker.code || blocker.reason_code || "blocked").trim();
    const message = String(blocker.message || blocker.reason || code).trim();
    if (!code || !message) return null;
    return {
      ...blocker,
      code,
      message,
      details: blocker.details ?? null,
    };
  });
}

function invalidWorkflow(reason, payload = null) {
  return {
    valid: false,
    reason,
    contractVersion: Number(payload?.contract_version || 0),
    businessUnitId: null,
    pipelineRunId: null,
    status: "blocked",
    capabilities: {
      can_approve: false,
      can_apply: false,
      can_rollback: false,
    },
    summary: {},
    latestOperation: null,
    blockers: [],
    rows: [],
  };
}

export function normalizeRmsRecommendationWorkflow(
  payload,
  { businessUnitId, pipelineRunId } = {}
) {
  if (!isPlainObject(payload) || Number(payload.contract_version) !== 1) {
    return invalidWorkflow("invalid_contract", payload);
  }
  const normalizedBusinessUnitId = normalizePositiveId(payload.business_unit_id);
  const normalizedPipelineRunId = normalizePositiveId(payload.pipeline_run_id);
  const expectedBusinessUnitId = normalizePositiveId(businessUnitId);
  const expectedPipelineRunId = normalizePositiveId(pipelineRunId);
  if (
    !normalizedBusinessUnitId ||
    !normalizedPipelineRunId ||
    (expectedBusinessUnitId && normalizedBusinessUnitId !== expectedBusinessUnitId) ||
    (expectedPipelineRunId && normalizedPipelineRunId !== expectedPipelineRunId)
  ) {
    return invalidWorkflow("identity_mismatch", payload);
  }
  const status = String(payload.status || "").trim().toLowerCase();
  if (!WORKFLOW_STATUSES.has(status)) {
    return invalidWorkflow("invalid_status", payload);
  }
  if (!isPlainObject(payload.capabilities)) {
    return invalidWorkflow("missing_capabilities", payload);
  }
  const capabilitiesAreExact = Object.values(CAPABILITY_BY_ACTION).every(
    (field) => typeof payload.capabilities[field] === "boolean"
  );
  const blockers = normalizeBlockers(payload.blockers);
  if (
    !capabilitiesAreExact ||
    !isPlainObject(payload.summary) ||
    !Array.isArray(payload.rows) ||
    blockers == null ||
    (payload.latest_operation != null && !isPlainObject(payload.latest_operation))
  ) {
    return invalidWorkflow("invalid_shape", payload);
  }

  return {
    valid: true,
    reason: null,
    contractVersion: 1,
    businessUnitId: normalizedBusinessUnitId,
    pipelineRunId: normalizedPipelineRunId,
    status,
    capabilities: {
      can_approve: payload.capabilities.can_approve,
      can_apply: payload.capabilities.can_apply,
      can_rollback: payload.capabilities.can_rollback,
    },
    summary: payload.summary,
    latestOperation: payload.latest_operation || null,
    blockers,
    rows: payload.rows,
  };
}

export function normalizeRmsWorkflowPreview(payload, expectedAction) {
  const action = String(expectedAction || "").trim().toLowerCase();
  if (
    !WORKFLOW_ACTION_SET.has(action) ||
    !isPlainObject(payload) ||
    Number(payload.contract_version) !== 1 ||
    payload.action !== action ||
    typeof payload.preview_token !== "string" ||
    !payload.preview_token.trim() ||
    typeof payload.expires_at !== "string" ||
    !Number.isFinite(new Date(payload.expires_at).getTime()) ||
    typeof payload.plan_hash !== "string" ||
    !/^[0-9a-f]{64}$/.test(payload.plan_hash) ||
    !isPlainObject(payload.summary) ||
    !Array.isArray(payload.rows)
  ) {
    return { valid: false, reason: "invalid_preview_contract", action };
  }
  const blockers = normalizeBlockers(payload.blockers);
  if (blockers == null) {
    return { valid: false, reason: "invalid_preview_blockers", action };
  }
  return {
    valid: true,
    reason: null,
    contractVersion: 1,
    action,
    previewToken: payload.preview_token,
    expiresAt: payload.expires_at,
    planHash: payload.plan_hash,
    summary: payload.summary,
    rows: payload.rows,
    blockers,
  };
}

export function normalizeRmsWorkflowExecutionResult(
  payload,
  { action, businessUnitId, pipelineRunId, idempotencyKey } = {}
) {
  const normalizedAction = String(action || "").trim().toLowerCase();
  const expectedIdempotencyKey = String(idempotencyKey || "").trim();
  const operation = payload?.operation;
  if (
    !WORKFLOW_ACTION_SET.has(normalizedAction) ||
    !isPlainObject(payload) ||
    Number(payload.contract_version) !== 1 ||
    !isPlainObject(operation) ||
    operation.id == null ||
    String(operation.action || "").trim().toLowerCase() !== normalizedAction ||
    typeof operation.status !== "string" ||
    !operation.status.trim() ||
    String(operation.idempotency_key || "").trim() !== expectedIdempotencyKey ||
    !isPlainObject(operation.summary)
  ) {
    return { valid: false, reason: "invalid_execution_contract" };
  }
  const workflow = normalizeRmsRecommendationWorkflow(payload.workflow, {
    businessUnitId,
    pipelineRunId,
  });
  if (!workflow.valid) {
    return { valid: false, reason: "invalid_execution_workflow" };
  }
  return {
    valid: true,
    reason: null,
    contractVersion: 1,
    workflow,
    operation,
  };
}

export function canRunRmsWorkflowAction(workflow, action) {
  const normalizedAction = String(action || "").trim().toLowerCase();
  const capability = CAPABILITY_BY_ACTION[normalizedAction];
  return Boolean(
    workflow?.valid &&
      capability &&
      workflow.capabilities?.[capability] === true &&
      workflow.blockers?.length === 0
  );
}

export function canExecuteRmsWorkflowPreview(preview, now = Date.now()) {
  return Boolean(
    preview?.valid &&
      preview.blockers?.length === 0 &&
      new Date(preview.expiresAt).getTime() > Number(now)
  );
}

export function buildRmsWorkflowPreviewPayload({ businessUnitId, pipelineRunId }) {
  return {
    business_unit_id: normalizePositiveId(businessUnitId),
    pipeline_run_id: normalizePositiveId(pipelineRunId),
  };
}

export function buildRmsWorkflowExecutePayload({
  businessUnitId,
  previewToken,
  idempotencyKey,
  note,
}) {
  const payload = {
    business_unit_id: normalizePositiveId(businessUnitId),
    preview_token: String(previewToken || "").trim(),
    idempotency_key: String(idempotencyKey || "").trim(),
  };
  const normalizedNote = String(note || "").trim();
  if (normalizedNote) payload.note = normalizedNote;
  return payload;
}

export function createRmsWorkflowIdempotencyKey() {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `rms-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function getRmsWorkflowActionCopy(action) {
  return ACTION_STATUS_COPY[action] || ACTION_STATUS_COPY.approve;
}

export function getRmsWorkflowError(error, fallbackMessage) {
  const envelope = error?.response?.data?.error;
  const code = String(envelope?.code || error?.response?.data?.code || "").trim();
  const backendMessage = String(
    envelope?.message || error?.response?.data?.message || ""
  ).trim();
  return {
    status: Number(error?.response?.status || 0) || null,
    code: code || null,
    message:
      WORKFLOW_ERROR_COPY[code] ||
      backendMessage ||
      error?.message ||
      fallbackMessage,
    details: envelope?.details ?? error?.response?.data?.details ?? null,
    requestId: envelope?.request_id ?? null,
  };
}

export function getRmsWorkflowStatusTone(status) {
  const normalized = String(status || "").toLowerCase();
  if (["approved", "applied", "rolled_back"].includes(normalized)) {
    return "success";
  }
  if (
    [
      "pending_approval",
      "queued",
      "pending",
      "processing",
    ].includes(normalized)
  ) {
    return "warn";
  }
  if (
    [
      "blocked",
      "failed",
      "rollback_blocked",
    ].includes(normalized)
  ) {
    return "danger";
  }
  return "soft";
}

export function resolveRmsWorkflowRowStatus(row) {
  return String(
    row?.workflow_status || row?.status || row?.operation_status || ""
  ).trim().toLowerCase() || null;
}

export { WORKFLOW_ACTIONS };
